/**
 * Realtime preview: renders the timeline into a canvas and plays it back with synchronised audio.
 *
 * Clock: while playing, time is derived from `AudioContext.currentTime` (compensated for output
 * latency) so picture follows the audible mix; before the context is allowed to run (autoplay
 * policy), `performance.now()` is used and playback re-anchors once audio starts.
 *
 * Rendering is demand-driven: a rAF loop runs only while playing or when a render was requested
 * (seek, document change, a video finished seeking, an image loaded). An idle editor costs nothing.
 */
import {
  forEachLayer,
  fpsValue,
  resolveAudio,
  resolveFrame,
  sequenceDuration,
  type AudioSegment,
  type FrameDescription,
  type LayerNode,
  type Project,
} from '@ve/core';
import { AudioBufferCache } from '../audio/audio-buffer-cache.ts';
import { scheduleAudio, type ScheduledAudio } from '../audio/schedule.ts';
import {
  Compositor,
  type LayerGeometry,
  type LayerTexture,
  type TextureProvider,
} from '../compositor/compositor.ts';
import { TransferCache } from '../media/color.ts';
import { ImageCache } from '../media/image-cache.ts';
import { logicalSize } from '../media/logical-size.ts';
import { VideoPool } from '../media/video-pool.ts';
import { inputKey } from '../media/input.ts';
import type { EngineEvent, MediaInput, MediaResolver } from '../types.ts';

export interface PreviewEngineOptions {
  canvas: HTMLCanvasElement;
  resolveMedia: MediaResolver;
}

export type PreviewResolution = 1 | 0.5 | 0.25;

/** Audio is scheduled this far ahead of "now" to avoid clicks/late starts. */
const SCHEDULE_LEAD = 0.06;
/** How far ahead upcoming video layers are pre-rolled. */
const PREROLL_SECONDS = 1;
const PREROLL_INTERVAL_MS = 200;

export class PreviewEngine {
  private readonly canvas: HTMLCanvasElement;
  private readonly resolveMedia: MediaResolver;
  private readonly compositor: Compositor;
  private readonly pool: VideoPool;
  private readonly images: ImageCache;
  private readonly transfers: TransferCache;
  private readonly audioCtx: AudioContext;
  private readonly masterGain: GainNode;
  private readonly audioCache: AudioBufferCache;

  private project: Project | null = null;
  private sequenceId = '';
  private fps = 30;
  private durationFrames = 0;
  private time = 0;
  private isPlaying = false;
  private loop: { start: number; end: number } | null = null;
  private resolution: PreviewResolution = 1;
  private useProxies = true;
  private disposed = false;

  private rafId: number | null = null;
  private renderPending = false;
  private lastEmitted: EngineEvent | null = null;
  private listeners = new Set<(e: EngineEvent) => void>();
  private renderListeners = new Set<() => void>();

  // Clock anchoring.
  private anchorTimeline = 0;
  private anchorClock = 0;
  private clockSource: 'audio' | 'perf' = 'perf';

  // Audio.
  private scheduled: ScheduledAudio | null = null;
  private audioPlan: { project: Project; sequenceId: string; segments: AudioSegment[] } | null = null;
  private rescheduleTimer: ReturnType<typeof setTimeout> | null = null;
  private prefetchTimer: ReturnType<typeof setTimeout> | null = null;

  // Per-project derived data.
  private speedByClip = new Map<string, number>();
  private speedFor: Project | null = null;
  private lastPreroll = 0;
  private lastDescription: FrameDescription | null = null;

  constructor(options: PreviewEngineOptions) {
    this.canvas = options.canvas;
    this.resolveMedia = options.resolveMedia;
    this.compositor = new Compositor(this.canvas, { onContextRestored: () => this.requestRender() });
    this.pool = new VideoPool(() => this.requestRender());
    this.images = new ImageCache(() => this.requestRender());
    this.transfers = new TransferCache(() => this.requestRender());
    this.audioCtx = new AudioContext({ latencyHint: 'interactive' });
    this.masterGain = this.audioCtx.createGain();
    this.masterGain.connect(this.audioCtx.destination);
    this.audioCache = new AudioBufferCache(this.audioCtx, undefined, () => this.onAudioLoaded());
  }

  // -------------------------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------------------------

  get frame(): number {
    return Math.floor(this.time * this.fps + 1e-6);
  }

  get playing(): boolean {
    return this.isPlaying;
  }

  /** Length of the current sequence in frames. */
  get duration(): number {
    return this.durationFrames;
  }

  /** Cheap; call on every document change. */
  setProject(project: Project, sequenceId: string): void {
    if (project === this.project && sequenceId === this.sequenceId) return;
    const sizeChanged =
      !this.project ||
      project.settings.width !== this.project.settings.width ||
      project.settings.height !== this.project.settings.height;
    const seqChanged = sequenceId !== this.sequenceId;
    this.project = project;
    this.sequenceId = sequenceId;
    this.fps = fpsValue(project.settings.fps);
    this.durationFrames = project.sequences[sequenceId] ? sequenceDuration(project, sequenceId) : 0;
    if (sizeChanged) this.applyCanvasSize();
    if (seqChanged && this.isPlaying) this.pause();
    if (this.isPlaying) this.debounceReschedule();
    this.debouncePrefetch();
    this.requestRender();
  }

  /**
   * Call when the sources of a media item changed (e.g. its proxy became ready, the file was
   * re-opened or access was restored). Elements and failures for it are reset.
   */
  invalidateMedia(mediaId: string): void {
    this.images.clearFailures();
    this.audioCache.clearFailures();
    this.pool.invalidateMedia(mediaId);
    const sources = this.resolveMedia(mediaId);
    if (sources) {
      const keys = new Set([sources.master, sources.proxy].filter((i) => i !== undefined).map(inputKey));
      this.pool.invalidate((k) => keys.has(k));
    }
    this.audioPlan = null;
    this.debouncePrefetch();
    if (this.isPlaying) this.debounceReschedule();
    this.requestRender();
  }

  play(): void {
    if (!this.project || this.isPlaying || this.disposed) return;
    const endFrame = this.loop ? this.loop.end : this.durationFrames;
    if (this.frame >= endFrame - 1) this.time = (this.loop?.start ?? 0) / this.fps;
    this.isPlaying = true;
    this.anchor(this.time);
    if (this.audioCtx.state !== 'running') {
      void this.audioCtx.resume().then(() => {
        if (this.isPlaying && this.clockSource === 'perf') this.reanchorAndReschedule();
      });
    }
    this.rescheduleAudio();
    this.emit();
    this.requestRender();
  }

  pause(): void {
    if (!this.isPlaying) return;
    this.time = this.clockTime();
    this.time = this.frame / this.fps;
    this.isPlaying = false;
    this.stopAudio();
    this.pool.pauseAll();
    this.emit();
    this.requestRender();
  }

  togglePlay(): void {
    if (this.isPlaying) this.pause();
    else this.play();
  }

  seek(frame: number): void {
    const max = Math.max(0, this.durationFrames);
    const f = Math.max(0, Math.min(max, Math.round(frame)));
    this.time = f / this.fps;
    if (this.isPlaying) this.reanchorAndReschedule(this.time);
    this.emit();
    this.requestRender();
  }

  setLoop(range: { start: number; end: number } | null): void {
    this.loop = range && range.end > range.start ? { ...range } : null;
    if (this.isPlaying) this.reanchorAndReschedule();
  }

  setPreviewResolution(scale: PreviewResolution): void {
    if (scale === this.resolution) return;
    this.resolution = scale;
    this.applyCanvasSize();
    this.requestRender();
  }

  setUseProxies(use: boolean): void {
    if (use === this.useProxies) return;
    this.useProxies = use;
    this.requestRender();
  }

  /** Master output volume (linear). */
  setVolume(volume: number): void {
    this.masterGain.gain.value = Math.max(0, volume);
  }

  subscribe(listener: (e: EngineEvent) => void): () => void {
    this.listeners.add(listener);
    listener({ frame: this.frame, playing: this.isPlaying });
    return () => this.listeners.delete(listener);
  }

  /** Geometry of a top-level layer as drawn in the last rendered frame (project pixel space). */
  /**
   * Called after every rendered frame (also for re-renders of the same frame, e.g. after an edit).
   * Use for UI that depends on render output, such as on-canvas transform handles.
   */
  onRender(listener: () => void): () => void {
    this.renderListeners.add(listener);
    return () => this.renderListeners.delete(listener);
  }

  getLayerGeometry(clipId: string): LayerGeometry | null {
    return this.compositor.getLayerGeometry(clipId);
  }

  /** The description of the last rendered frame (useful for hit-testing in the viewer). */
  get lastFrame(): FrameDescription | null {
    return this.lastDescription;
  }

  /** Forces a redraw on the next animation frame. */
  requestRender(): void {
    if (this.disposed) return;
    this.renderPending = true;
    this.ensureLoop();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (this.rescheduleTimer) clearTimeout(this.rescheduleTimer);
    if (this.prefetchTimer) clearTimeout(this.prefetchTimer);
    this.stopAudio();
    this.pool.dispose();
    this.images.clear();
    this.transfers.clear();
    this.compositor.dispose();
    void this.audioCtx.close().catch(() => undefined);
    this.listeners.clear();
    this.renderListeners.clear();
  }

  // -------------------------------------------------------------------------------------------
  // Clock
  // -------------------------------------------------------------------------------------------

  private now(): number {
    return this.clockSource === 'audio' ? this.audioCtx.currentTime : performance.now() / 1000;
  }

  private latency(): number {
    if (this.clockSource !== 'audio') return 0;
    return this.audioCtx.outputLatency || this.audioCtx.baseLatency || 0;
  }

  private anchor(timelineTime: number): void {
    this.clockSource = this.audioCtx.state === 'running' ? 'audio' : 'perf';
    this.anchorTimeline = timelineTime;
    this.anchorClock = this.now() + (this.clockSource === 'audio' ? SCHEDULE_LEAD : 0);
  }

  /** Timeline time currently visible/audible. */
  private clockTime(): number {
    if (!this.isPlaying) return this.time;
    const elapsed = this.now() - this.latency() - this.anchorClock;
    return this.anchorTimeline + Math.max(0, elapsed);
  }

  private reanchorAndReschedule(at?: number): void {
    const t = at ?? this.clockTime();
    this.anchor(t);
    this.time = t;
    this.rescheduleAudio();
  }

  // -------------------------------------------------------------------------------------------
  // Audio
  // -------------------------------------------------------------------------------------------

  private segments(): AudioSegment[] {
    const project = this.project;
    if (!project) return [];
    const plan = this.audioPlan;
    if (plan && plan.project === project && plan.sequenceId === this.sequenceId) return plan.segments;
    const segments = resolveAudio(project, this.sequenceId);
    this.audioPlan = { project, sequenceId: this.sequenceId, segments };
    return segments;
  }

  /** Audio source of a media item: the explicit audio file, else the media file itself. */
  private audioInput(mediaId: string): MediaInput | undefined {
    const sources = this.resolveMedia(mediaId);
    return sources ? (sources.audio ?? sources.master) : undefined;
  }

  private audioInputs(mediaIds: Iterable<string>): MediaInput[] {
    const out: MediaInput[] = [];
    for (const id of mediaIds) {
      const input = this.audioInput(id);
      if (input !== undefined) out.push(input);
    }
    return out;
  }

  private rescheduleAudio(): void {
    this.stopAudio();
    if (!this.isPlaying || this.clockSource !== 'audio') return;
    const segments = this.segments();
    this.audioCache.pin(this.audioInputs(segments.map((s) => s.mediaId)));
    this.scheduled = scheduleAudio(
      this.audioCtx,
      this.masterGain,
      segments,
      (id) => {
        const input = this.audioInput(id);
        return input !== undefined ? this.audioCache.get(input) : undefined;
      },
      {
        timelineTime: this.anchorTimeline,
        contextTime: this.anchorClock,
        endTime: this.loop ? this.loop.end / this.fps : undefined,
      },
    );
    const missing = this.audioInputs(this.scheduled.missing).filter(
      (i) => !this.audioCache.hasFailed(i) && !this.audioCache.isSilent(i),
    );
    if (missing.length > 0) void this.audioCache.prefetch(missing);
  }

  private stopAudio(): void {
    this.scheduled?.stop();
    this.scheduled = null;
  }

  private onAudioLoaded(): void {
    if (this.isPlaying) this.debounceReschedule();
  }

  private debounceReschedule(): void {
    if (this.rescheduleTimer) clearTimeout(this.rescheduleTimer);
    this.rescheduleTimer = setTimeout(() => {
      this.rescheduleTimer = null;
      if (this.isPlaying) this.reanchorAndReschedule();
    }, 60);
  }

  private debouncePrefetch(): void {
    if (this.prefetchTimer) clearTimeout(this.prefetchTimer);
    this.prefetchTimer = setTimeout(() => {
      this.prefetchTimer = null;
      void this.audioCache.prefetch(this.audioInputs(this.segments().map((s) => s.mediaId)));
    }, 300);
  }

  // -------------------------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------------------------

  private applyCanvasSize(): void {
    if (!this.project) return;
    const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);
    this.canvas.width = even(this.project.settings.width * this.resolution);
    this.canvas.height = even(this.project.settings.height * this.resolution);
  }

  private ensureLoop(): void {
    if (this.rafId !== null || this.disposed) return;
    this.rafId = requestAnimationFrame(() => this.tick());
  }

  private tick(): void {
    this.rafId = null;
    if (this.disposed) return;
    if (this.isPlaying) {
      let t = this.clockTime();
      if (this.loop && t * this.fps >= this.loop.end) {
        t = this.loop.start / this.fps;
        this.reanchorAndReschedule(t);
      } else if (t * this.fps >= this.durationFrames) {
        this.pause();
        this.time = Math.max(0, this.durationFrames - 1) / this.fps;
        this.emit();
        this.requestRender();
        return;
      }
      this.time = t;
    }
    if (this.isPlaying || this.renderPending) {
      this.renderPending = false;
      this.renderCurrent();
      this.emit();
      for (const l of this.renderListeners) l();
    }
    if (this.isPlaying || this.renderPending) this.ensureLoop();
  }

  private speedOf(clipId: string): number {
    const project = this.project;
    if (!project) return 1;
    if (this.speedFor !== project) {
      this.speedByClip.clear();
      for (const seq of Object.values(project.sequences)) {
        for (const track of seq.videoTracks) {
          for (const c of track.clips) if (c.type === 'video') this.speedByClip.set(c.id, c.speed);
        }
      }
      this.speedFor = project;
    }
    return this.speedByClip.get(clipId) ?? 1;
  }

  private videoInput(mediaId: string): MediaInput | undefined {
    const sources = this.resolveMedia(mediaId);
    if (!sources) return undefined;
    return this.useProxies && sources.proxy ? sources.proxy : sources.master;
  }

  private syncVideo(layer: LayerNode, playing: boolean, prepareOnly: boolean): void {
    if (layer.source.type !== 'video' || !this.project) return;
    const input = this.videoInput(layer.source.mediaId);
    if (input === undefined) return;
    const asset = this.project.media[layer.source.mediaId];
    const params = {
      playing,
      mediaId: layer.source.mediaId,
      fps: this.fps,
      rate: this.speedOf(layer.clipId),
      mediaFps: asset?.fps,
      duration: asset?.duration,
    };
    if (prepareOnly) this.pool.prepare(layer.key, input, layer.source.time, params);
    else this.pool.sync(layer.key, input, layer.source.time, params);
  }

  private renderCurrent(): void {
    const project = this.project;
    if (!project) return;
    const frame = this.frame;
    const desc = resolveFrame(project, this.sequenceId, frame);
    this.lastDescription = desc;

    const active = new Set<string>();
    forEachLayer(desc.layers, (layer) => {
      if (layer.source.type !== 'video') return;
      active.add(layer.key);
      this.syncVideo(layer, this.isPlaying, false);
    });

    const nowMs = performance.now();
    if (this.isPlaying && nowMs - this.lastPreroll > PREROLL_INTERVAL_MS) {
      this.lastPreroll = nowMs;
      const ahead = resolveFrame(project, this.sequenceId, frame + Math.round(this.fps * PREROLL_SECONDS));
      forEachLayer(ahead.layers, (layer) => {
        if (layer.source.type !== 'video' || active.has(layer.key)) return;
        active.add(layer.key);
        this.syncVideo(layer, false, true);
      });
    }

    this.compositor.render(desc, this.provider);
    this.pool.sweep(active);
  }

  private readonly provider: TextureProvider = {
    getTexture: (layer: LayerNode): LayerTexture | null => {
      const project = this.project;
      if (!project) return null;
      if (layer.source.type === 'video') {
        const el = this.pool.frame(layer.key);
        const input = this.pool.input(layer.key);
        if (!el || input === undefined) return null;
        // Wait for the transfer function rather than flash an HDR frame decoded as SDR.
        const transfer = this.transfers.peek(input);
        if (transfer === undefined) return null;
        const size = logicalSize(project.media[layer.source.mediaId], el.videoWidth, el.videoHeight);
        return { source: el, ...size, transfer };
      }
      if (layer.source.type === 'image') {
        const master = this.resolveMedia(layer.source.mediaId)?.master;
        if (master === undefined) return null;
        const img = this.images.peek(master);
        if (!img) return null;
        const size = logicalSize(project.media[layer.source.mediaId], img.width, img.height);
        return { source: img.bitmap, ...size, cacheKey: `image:${img.key}`, version: img.key };
      }
      return null;
    },
  };

  private emit(): void {
    const e = { frame: this.frame, playing: this.isPlaying };
    const last = this.lastEmitted;
    if (last && last.frame === e.frame && last.playing === e.playing) return;
    this.lastEmitted = e;
    for (const l of this.listeners) l(e);
  }
}
